import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * Los componentes que sostienen la interfaz.
 *
 * Están todos en un archivo a propósito. Son diez piezas chicas que se leen de
 * corrido en dos minutos, y tenerlas juntas es lo que hace evidente cuándo dos
 * empiezan a parecerse demasiado. Repartidas en diez archivos aparecen
 * variantes casi iguales sin que nadie se entere: así se llegó a tener 147
 * bordes sueltos y dos radios distintos conviviendo.
 *
 * Todo sale de los tokens de `styles/index.css`. Ninguna medida ni color se
 * escribe acá a mano.
 */

// ---------------------------------------------------------------------------
// Botones
// ---------------------------------------------------------------------------

type Jerarquia = 'primario' | 'secundario' | 'destructivo' | 'plano';

const JERARQUIAS: Record<Jerarquia, string> = {
  // La acción de la pantalla. Una por pantalla, o ninguna.
  primario:
    'bg-[var(--accion)] text-[var(--sobre-accion)] font-medium hover:bg-[var(--accion-viva)]',
  // Lo que se hace seguido y no compromete nada.
  secundario:
    'bg-[var(--superficie)] text-[var(--tinta)] border border-[var(--linea-fuerte)] hover:bg-[var(--superficie-suave)]',
  // Rojo SÓLO para lo irreversible: eliminar, anular, revertir. Nunca para
  // facturar, cobrar, guardar ni navegar. El rojo que se usa de más deja de
  // avisar cuando hace falta.
  destructivo: 'bg-[var(--peligro-solido)] text-[var(--sobre-peligro)] font-medium',
  // Sin caja: para acciones de apoyo dentro de una fila.
  plano: 'text-[var(--tinta-suave)] hover:text-[var(--tinta)] underline underline-offset-2',
};

export function Boton({
  jerarquia = 'secundario',
  anchoCompleto = false,
  className,
  type = 'button',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  jerarquia?: Jerarquia;
  anchoCompleto?: boolean;
}) {
  return (
    <button
      type={type}
      // 44 px es el mínimo táctil, y no se negocia: por debajo de eso se yerra
      // el botón con el pulgar, que en el campo se hace con guantes.
      className={cn(
        'inline-flex min-h-11 items-center justify-center gap-2 rounded-[var(--radius-control)] px-4 text-sm transition-colors disabled:opacity-50',
        JERARQUIAS[jerarquia],
        anchoCompleto && 'w-full',
        className,
      )}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// Superficies
// ---------------------------------------------------------------------------

/**
 * Una superficie apoyada sobre el fondo.
 *
 * Se separa por FONDO, no por borde. El borde queda como opción para cuando
 * hay que delimitar algo sobre un fondo del mismo color, no como envoltorio
 * por omisión: encerrar cada bloque en un rectángulo convierte la pantalla en
 * una reja y hace que nada resalte, porque todo resalta igual.
 */
export function Tarjeta({
  conBorde = false,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { conBorde?: boolean }) {
  return (
    <div
      className={cn('superficie p-4', conBorde && 'border border-[var(--linea)]', className)}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * El encabezado de una pantalla.
 *
 * Título grande, y el «volver» arriba a la izquierda donde siempre estuvo. La
 * navegación es una sola: no hay barra inferior, no hay hamburguesa. Inicio es
 * el centro y de las pantallas se vuelve.
 */
export function EncabezadoPantalla({
  titulo,
  nota,
  onVolver,
  acciones,
}: {
  titulo: string;
  nota?: ReactNode;
  onVolver?: () => void;
  acciones?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-2">
      {onVolver && (
        <button
          type="button"
          onClick={onVolver}
          className="-ml-1 inline-flex min-h-11 items-center self-start px-1 text-sm text-[var(--tinta-suave)]"
          data-testid="volver"
        >
          ‹ Volver
        </button>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">{titulo}</h1>
          {nota && <p className="mt-0.5 text-sm text-[var(--tinta-suave)]">{nota}</p>}
        </div>
        {acciones}
      </div>
    </header>
  );
}

/**
 * El título de una sección dentro de una pantalla.
 *
 * Separa por espacio y por tipografía, no metiendo la sección en una caja.
 * Ver detalle tenía seis cajas apiladas y se leía como seis pantallas.
 */
export function EncabezadoSeccion({
  titulo,
  nota,
  acciones,
}: {
  titulo: string;
  nota?: ReactNode;
  acciones?: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--tinta-suave)]">
          {titulo}
        </h2>
        {nota && <p className="mt-0.5 text-sm text-[var(--tinta-suave)]">{nota}</p>}
      </div>
      {acciones}
    </div>
  );
}

/** Una sección: su título y su contenido, separados por aire. */
export function Seccion({
  titulo,
  nota,
  acciones,
  className,
  children,
}: {
  titulo: string;
  nota?: ReactNode;
  acciones?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn('flex flex-col gap-3', className)}>
      <EncabezadoSeccion titulo={titulo} nota={nota} acciones={acciones} />
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Formulario
// ---------------------------------------------------------------------------

/** Rótulo + control. El rótulo arriba y siempre presente: un `placeholder` que
 *  hace de rótulo desaparece justo cuando se está escribiendo. */
export function Campo({
  rotulo,
  ayuda,
  error,
  children,
}: {
  rotulo: string;
  ayuda?: ReactNode;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{rotulo}</span>
      {children}
      {error ? (
        <span className="text-xs text-[var(--peligro)]">{error}</span>
      ) : (
        ayuda && <span className="text-xs text-[var(--tinta-suave)]">{ayuda}</span>
      )}
    </label>
  );
}

/** El estilo de todo lo que se escribe o se elige. Una sola definición. */
export const CONTROL =
  'min-h-11 w-full rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] bg-[var(--superficie)] px-3 text-base text-[var(--tinta)] disabled:opacity-50';

export function Entrada({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(CONTROL, className)} {...props} />;
}

export function Seleccion({ className, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cn(CONTROL, className)} {...props} />;
}

export function AreaTexto({
  className,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(CONTROL, 'min-h-20 py-2', className)} {...props} />;
}

// ---------------------------------------------------------------------------
// Estados de la pantalla
// ---------------------------------------------------------------------------

/** Cuando no hay nada que mostrar. Dice qué falta y, si se puede, cómo empezar. */
export function SinNada({
  texto,
  accion,
  testId,
}: {
  texto: string;
  accion?: ReactNode;
  testId?: string;
}) {
  return (
    <div
      className="superficie-suave flex flex-col items-center gap-3 px-4 py-8 text-center"
      data-testid={testId}
    >
      <p className="text-sm text-[var(--tinta-suave)]">{texto}</p>
      {accion}
    </div>
  );
}

/** Un mensaje de error de la pantalla. */
export function Error({ children, testId }: { children: ReactNode; testId?: string }) {
  if (!children) return null;
  return (
    <p
      role="alert"
      className="rounded-[var(--radius-control)] bg-[var(--peligro-fondo)] px-3 py-2 text-sm text-[var(--peligro)]"
      data-testid={testId}
    >
      {children}
    </p>
  );
}

/**
 * Plata.
 *
 * Los importes se comparan en columna, así que van con cifras de ancho fijo:
 * sin eso, un 1 y un 8 ocupan distinto y los números no alinean. Y el dinero
 * tiene que encontrarse rápido, así que es semibold — pero no enorme: lo que
 * se busca es distinguirlo del texto de al lado, no gritar.
 */
export function Importe({
  children,
  pendiente = false,
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & { pendiente?: boolean }) {
  return (
    <span
      className={cn('font-semibold tabular-nums', pendiente && 'text-[var(--aviso)]', className)}
      {...props}
    >
      {children}
    </span>
  );
}
